import type { ReactNode } from 'react';
import { ArrowLeft, Save, Undo2, Redo2, CheckCircle2, SlidersHorizontal } from 'lucide-react';

export type EditorSaveState = 'idle' | 'dirty' | 'saved';

export interface EditorToolbarTitle {
  /** Current title/subject value. */
  value: string;
  /** Live update while typing. */
  onChange: (value: string) => void;
  /** Fired when the field loses focus — persist there (avoids save-per-keystroke). */
  onCommit: (value: string) => void;
  placeholder: string;
}

export interface EditorToolbarProps {
  /** Destination label for the back button ("posts" / "emails"). */
  backTitle: string;
  onBack: () => void;
  /** Title / subject input config. */
  title: EditorToolbarTitle;
  /** Dirty flag state shown next to the title. */
  saveState: EditorSaveState;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  canSave: boolean;
  saving: boolean;
  /** Opens the inspector panel — shown on small screens where the panel is an overlay sheet. */
  onOpenInspector?: () => void;
  /** Extra buttons rendered after the divider, before Save (e.g. View, Build, Test send). */
  actions?: ReactNode;
  /** Primary action rendered right of Save (e.g. Publish/Update). */
  primaryAction?: ReactNode;
}

/**
 * Toolbar shared by the post and email block editors: back navigation, the
 * title/subject field, dirty state, undo/redo, and the save action. Content
 * kind-specific buttons are injected through `actions` / `primaryAction`.
 */
export function EditorToolbar({
  backTitle,
  onBack,
  title,
  saveState,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
  canSave,
  saving,
  onOpenInspector,
  actions,
  primaryAction,
}: EditorToolbarProps) {
  return (
    <div className="relative z-20 flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-line bg-bg-soft/70 px-3 py-2 backdrop-blur-md">
      {/* Back */}
      <button
        type="button"
        title={`Go to ${backTitle}`}
        onClick={onBack}
        className="btn-ghost h-8 w-8 shrink-0 rounded-lg px-0"
      >
        <ArrowLeft size={15} />
      </button>

      {/* Styles / inspector — mobile only (desktop has the side panel) */}
      {onOpenInspector && (
        <button
          type="button"
          title="Block styles"
          onClick={onOpenInspector}
          className="btn-ghost h-8 w-8 shrink-0 rounded-lg px-0 lg:hidden"
        >
          <SlidersHorizontal size={15} />
        </button>
      )}

      <span className="mx-1 h-5 w-px shrink-0 bg-line" />

      {/* Title / subject */}
      <input
        value={title.value}
        onChange={(e) => title.onChange(e.target.value)}
        onBlur={(e) => title.onCommit(e.target.value)}
        className="h-8 min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2.5 text-[15px] font-semibold tracking-tight text-ink outline-none transition-colors placeholder:font-medium placeholder:text-muted hover:bg-ink/[0.04] focus:border-accent-border focus:bg-ink/[0.03]"
        placeholder={title.placeholder}
      />

      {/* Save state */}
      <div className="ml-1 flex shrink-0 items-center">
        {saveState === 'dirty' && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-warn/10 px-2.5 py-1 text-[11px] font-semibold text-warn">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-warn" />
            Unsaved
          </span>
        )}
        {saveState === 'saved' && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-ok/10 px-2.5 py-1 text-[11px] font-semibold text-ok">
            <CheckCircle2 size={12} />
            Saved
          </span>
        )}
      </div>

      {/* Undo / Redo */}
      <div className="ml-2 flex shrink-0 items-center gap-0.5">
        <button
          onClick={onUndo}
          type="button"
          disabled={!canUndo}
          className="btn-ghost h-8 w-8 rounded-lg px-0 disabled:opacity-30"
          title="Undo (⌘Z)"
        >
          <Undo2 size={14} />
        </button>
        <button
          onClick={onRedo}
          type="button"
          disabled={!canRedo}
          className="btn-ghost h-8 w-8 rounded-lg px-0 disabled:opacity-30"
          title="Redo (⌘⇧Z)"
        >
          <Redo2 size={14} />
        </button>
      </div>

      <span className="mx-1.5 h-5 w-px shrink-0 bg-line" />

      {/* Content-kind actions (Preview / View / Build / Test send / …) */}
      <div className="flex shrink-0 items-center gap-1">{actions}</div>

      {/* Save */}
      <button
        type="button"
        onClick={onSave}
        disabled={saving || !canSave}
        className={`btn shrink-0 rounded-lg px-3 py-1.5 text-xs ${
          canSave && !saving
            ? 'bg-accent/10 text-accent hover:bg-accent/15'
            : 'text-muted hover:text-ink'
        }`}
        title="Save (⌘S)"
      >
        <Save size={13} />
        {saving ? 'Saving…' : 'Save'}
      </button>

      {/* Primary action (Publish / Update / …) */}
      {primaryAction && <div className="shrink-0">{primaryAction}</div>}
    </div>
  );
}
